/**
 * Static scaffold the WebContainer demo build needs but the LLM doesn't emit:
 * a full package.json with all build/runtime deps, a tsconfig, a webpack
 * config (mirroring jira-mock-setup's expected output), and the demo entry
 * (`src/demo/DemoEntry.tsx`).
 *
 * Per-project LLM-emitted source slots in on top of this scaffold at file-
 * tree assembly time. If the LLM emits any of these names, its version wins
 * (so an app can override the entry or webpack config if it really needs to).
 */

export interface ScaffoldFile {
  path: string;
  contents: string;
}

const PACKAGE_JSON = JSON.stringify(
  {
    name: 'forge-app-demo',
    private: true,
    version: '0.0.1',
    scripts: {
      start: 'webpack serve --config webpack.demo.config.js --mode development',
      build: 'webpack --config webpack.demo.config.js --mode production',
    },
    dependencies: {
      react: '^18.3.1',
      'react-dom': '^18.3.1',
      'react-router-dom': '^6.26.0',
      '@atlaskit/css-reset': 'latest',
      '@atlaskit/button': 'latest',
      '@atlaskit/lozenge': 'latest',
      '@atlaskit/page-header': 'latest',
    },
    devDependencies: {
      '@types/react': '^18.3.3',
      '@types/react-dom': '^18.3.0',
      'html-webpack-plugin': '^5.6.0',
      'ts-loader': '^9.5.1',
      typescript: '^5.4.5',
      webpack: '^5.94.0',
      'webpack-cli': '^5.1.4',
      'webpack-dev-server': '^5.0.4',
      'style-loader': '^4.0.0',
      'css-loader': '^7.1.2',
      buffer: '^6.0.3',
      process: '^0.11.10',
      'crypto-browserify': '^3.12.0',
      'stream-browserify': '^3.0.0',
    },
  },
  null,
  2,
);

const TSCONFIG = JSON.stringify(
  {
    compilerOptions: {
      target: 'ES2020',
      module: 'ESNext',
      moduleResolution: 'node',
      jsx: 'react-jsx',
      strict: true,
      esModuleInterop: true,
      skipLibCheck: true,
      forceConsistentCasingInFileNames: true,
      allowSyntheticDefaultImports: true,
      resolveJsonModule: true,
      isolatedModules: true,
    },
    include: ['src', 'packages/jira-mock/src'],
  },
  null,
  2,
);

const WEBPACK_CONFIG = `const path = require('path');
const webpack = require('webpack');
const HtmlWebpackPlugin = require('html-webpack-plugin');

const JIRA_MOCK_SRC = path.resolve(__dirname, 'packages/jira-mock/src');

module.exports = {
  entry: { Demo: './src/demo/DemoEntry.tsx' },

  output: {
    path: path.resolve(__dirname, 'build-demo'),
    filename: '[name]/app.js',
    clean: true,
    publicPath: 'auto',
  },

  resolve: {
    extensions: ['.ts', '.tsx', '.js', '.jsx'],
    alias: {
      '@forge/bridge': path.resolve(JIRA_MOCK_SRC, 'bridge.ts'),
      'otpl-atlas-kit': path.resolve(__dirname, 'src/stubs/otpl-atlas-kit.tsx'),
    },
    fallback: {
      buffer: require.resolve('buffer'),
      process: require.resolve('process/browser'),
      crypto: require.resolve('crypto-browserify'),
      stream: require.resolve('stream-browserify'),
      fs: false,
      path: false,
    },
  },

  module: {
    rules: [
      {
        test: /\\.tsx?$/,
        use: [{
          loader: 'ts-loader',
          options: {
            allowTsInNodeModules: true,
            transpileOnly: true,
          },
        }],
      },
      { test: /\\.css$/, use: ['style-loader', 'css-loader'] },
    ],
  },

  plugins: [
    new webpack.ProvidePlugin({
      process: 'process/browser',
      Buffer: ['buffer', 'Buffer'],
    }),
    new HtmlWebpackPlugin({
      // Emit at the build-demo root, not Demo/index.html — so the auto-injected
      // script src is a clean relative \`Demo/app.js\` that resolves correctly
      // when the iframe loads the demo at /api/projects/:id/demo/.
      filename: 'index.html',
      inject: true,
      chunks: ['Demo'],
      templateContent: \`<!doctype html>
<html>
  <head><meta charset="utf-8"><title>Forge app demo</title></head>
  <body><div id="root"></div></body>
</html>\`,
    }),
  ],

  devServer: {
    static: path.resolve(__dirname, 'build-demo'),
    port: 3111,
    host: '0.0.0.0',
    hot: false,
    historyApiFallback: { index: '/Demo/index.html' },
    client: { logging: 'warn', overlay: false },
  },
};
`;

const DEMO_ENTRY = `import React from 'react';
import { createRoot } from 'react-dom/client';
import { view } from '@forge/bridge';
import '@atlaskit/css-reset';
import { App } from '../App';

// Mirrors jira-mock-setup's DemoEntry pattern: \`view.theme.enable()\` first
// (no-op in jira-mock), then mount the same component tree the real Forge
// entry would.
void view.theme.enable().then(() => {
  const root = createRoot(document.getElementById('root')!);
  root.render(<App />);
});
`;

// Minimal stub for the otpl-atlas-kit workspace package. Webpack aliases
// 'otpl-atlas-kit' here so upstream code that imports from it resolves. Each
// export is a native-HTML/no-op approximation — enough to compile and render
// generated apps without pulling in the real package's design system. Replace
// with a proper vendored bundle when production-quality styling is needed.
const OTPL_ATLAS_KIT_STUB = `import React from 'react';

type AnyChildren = { children?: React.ReactNode };

export const GlobalPage: React.FC<AnyChildren> = ({ children }) => (
  <div className="otpl-global-page">{children}</div>
);

export const PageHeader: React.FC<{
  breadcrumbs?: React.ReactNode;
  title?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ breadcrumbs, title, actions }) => (
  <header className="otpl-page-header" style={{ padding: '12px 16px', borderBottom: '1px solid #ddd' }}>
    {breadcrumbs}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h1 style={{ margin: 0 }}>{title}</h1>
      <div>{actions}</div>
    </div>
  </header>
);

export const Breadcrumbs: React.FC<AnyChildren> = ({ children }) => (
  <nav className="otpl-breadcrumbs">{children}</nav>
);

export const BreadcrumbsItem: React.FC<{ text?: string; href?: string }> = ({ text, href }) =>
  href ? <a href={href}>{text}</a> : <span>{text}</span>;

export const SearchBox: React.FC<{
  placeholder?: string;
  onSearch?: (q: string) => void;
}> = ({ placeholder, onSearch }) => (
  <input
    type="search"
    placeholder={placeholder}
    onChange={(e) => onSearch?.(e.target.value)}
  />
);

export const button: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { appearance?: string }
> = ({ children, appearance: _a, ...rest }) => <button {...rest}>{children}</button>;

interface Column<T> {
  key: string;
  header: React.ReactNode;
  render?: (row: T) => React.ReactNode;
  width?: string | number;
}
export const Table = <T,>(props: {
  columns: Column<T>[];
  data: T[];
  testId?: string;
}): React.ReactElement => (
  <table data-testid={props.testId} style={{ width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr>
        {props.columns.map((c) => (
          <th key={c.key} style={{ width: c.width, textAlign: 'left', padding: '8px' }}>
            {c.header}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {props.data.map((row, i) => (
        <tr key={i}>
          {props.columns.map((c) => (
            <td key={c.key} style={{ padding: '8px', borderTop: '1px solid #eee' }}>
              {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export const Lozenge: React.FC<AnyChildren & { appearance?: string }> = ({ children, appearance }) => (
  <span data-appearance={appearance} style={{ padding: '2px 6px', border: '1px solid #ccc', borderRadius: 3 }}>
    {children}
  </span>
);

export const LoadingComponent: React.FC = () => <div>Loading…</div>;

export const EmptyState: React.FC<{
  header?: React.ReactNode;
  description?: React.ReactNode;
  primaryAction?: React.ReactNode;
}> = ({ header, description, primaryAction }) => (
  <div style={{ padding: 24, textAlign: 'center' }}>
    {header && <h2>{header}</h2>}
    {description && <p>{description}</p>}
    {primaryAction}
  </div>
);

export const Modal: React.FC<
  AnyChildren & { isOpen?: boolean; onClose?: () => void; heading?: React.ReactNode }
> = ({ isOpen, onClose, heading, children }) => {
  if (!isOpen) return null;
  return (
    <div
      role="dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ background: '#fff', padding: 24, minWidth: 320, borderRadius: 4 }}>
        {heading && <h3 style={{ marginTop: 0 }}>{heading}</h3>}
        {children}
        <button onClick={onClose} style={{ marginTop: 12 }}>Close</button>
      </div>
    </div>
  );
};

export class CloudAPIService {
  constructor(..._args: unknown[]) {}
  // Common verbs upstream code may try — all no-ops returning empty results.
  async get<T = unknown>(): Promise<T | null> { return null; }
  async post<T = unknown>(): Promise<T | null> { return null; }
  async put<T = unknown>(): Promise<T | null> { return null; }
  async delete<T = unknown>(): Promise<T | null> { return null; }
}
`;

export const scaffoldFiles: ScaffoldFile[] = [
  { path: 'package.json', contents: PACKAGE_JSON },
  { path: 'tsconfig.json', contents: TSCONFIG },
  { path: 'webpack.demo.config.js', contents: WEBPACK_CONFIG },
  { path: 'src/demo/DemoEntry.tsx', contents: DEMO_ENTRY },
  { path: 'src/stubs/otpl-atlas-kit.tsx', contents: OTPL_ATLAS_KIT_STUB },
];
